/**
 * useIndividualFinances
 *
 * CRUD hook for the `individual_finances` table.
 * Scoped to the current authenticated user via RLS.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export type FinanceType = "income" | "expense";

export const INCOME_CATEGORIES = [
  "Rent Payment",
  "Security Deposit",
  "Late Fee",
  "Pet Fee",
  "Parking Fee",
  "Other Income",
] as const;

export const EXPENSE_CATEGORIES = [
  "Repairs & Maintenance",
  "Insurance",
  "Property Tax",
  "Utilities",
  "Management Fee",
  "Mortgage / Interest",
  "Cleaning",
  "Supplies",
  "Advertising",
  "Legal / Accounting",
  "Other Expense",
] as const;

export type IncomeCategory = (typeof INCOME_CATEGORIES)[number];
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export interface FinanceEntry {
  id: string;
  user_id: string;
  unit_id: string | null;
  type: FinanceType;
  category: string;
  amount: number;
  entry_date: string;
  notes: string | null;
  receipt_url: string | null;
  created_at: string;
  updated_at: string;
  /** joined from units */
  unit?: { unit_number: string } | null;
}

export interface FinanceEntryInput {
  unit_id?: string | null;
  type: FinanceType;
  category: string;
  amount: number;
  entry_date: string;
  notes?: string | null;
  receipt_url?: string | null;
}

const QUERY_KEY = "individual-finances";

// ─── Fetch all entries ────────────────────────────────────────────────────────
async function fetchFinances(): Promise<FinanceEntry[]> {
  const { data, error } = await supabase
    .from("individual_finances")
    .select("*, unit:units(unit_number)")
    .order("entry_date", { ascending: false });

  if (error) throw error;
  return (data ?? []) as FinanceEntry[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useIndividualFinances() {
  const qc = useQueryClient();
  const { user } = useAuth();

  const query = useQuery<FinanceEntry[]>({
    queryKey: [QUERY_KEY, user?.id],
    enabled: !!user,
    queryFn: fetchFinances,
  });

  // ── Add entry ──────────────────────────────────────────────────────────────
  const add = useMutation({
    mutationFn: async (input: FinanceEntryInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("individual_finances")
        .insert({ ...input, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });

  // ── Delete entry ───────────────────────────────────────────────────────────
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("individual_finances")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });

  // ── Derived aggregates ────────────────────────────────────────────────────
  const entries = query.data ?? [];

  const totalIncome = entries
    .filter((e) => e.type === "income")
    .reduce((sum, e) => sum + e.amount, 0);

  const totalExpenses = entries
    .filter((e) => e.type === "expense")
    .reduce((sum, e) => sum + e.amount, 0);

  const netBalance = totalIncome - totalExpenses;

  /** Per-unit totals */
  const byUnit = entries.reduce<
    Record<string, { income: number; expense: number; net: number }>
  >((acc, e) => {
    const key = e.unit_id ?? "__no_unit__";
    if (!acc[key]) acc[key] = { income: 0, expense: 0, net: 0 };
    if (e.type === "income") acc[key].income += e.amount;
    else acc[key].expense += e.amount;
    acc[key].net = acc[key].income - acc[key].expense;
    return acc;
  }, {});

  return {
    entries,
    isLoading: query.isLoading,
    isError: query.isError,
    totalIncome,
    totalExpenses,
    netBalance,
    byUnit,
    add,
    remove,
  };
}
